
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Save, FileText, Printer, MapPin, User, X, Phone, Copy, Truck } from 'lucide-react';
import ProductManager from './ProductManager';
import PriceEstimator from './PriceEstimator';
import SenderSection from './forms/SenderSection';
import RecipientDeliverySection from './forms/RecipientDeliverySection';
import { mockAddresses } from '@/api/mockData';
import { useToast } from '@/hooks/use-toast';
import { useOrderForm } from '@/hooks/useOrderForm';
import { PROVINCES, DISTRICTS, WARDS, SHIPPING_SERVICES, WEIGHT_UNITS } from '@/constants/orderConstants';
import { validateOrderForm, formatWeight, generateOrderId } from '@/utils/orderUtils';
import type { OrderProduct, Address } from '@/types';

// Mock data cho địa chỉ người gửi
const mockSenderAddresses = [
  {
    id: '1',
    name: 'Nguyễn Văn A',
    phone: '0901234567',
    province: 'TP.HCM',
    district: 'Quận 1',
    ward: 'Phường 1',
    detailedAddress: '123 Đường ABC',
    fullAddress: '123 Đường ABC, Phường 1, Quận 1, TP.HCM',
    type: 'sender' as const
  },
  {
    id: '2', 
    name: 'Trần Thị B',
    phone: '0987654321',
    province: 'TP.HCM',
    district: 'Quận 3',
    ward: 'Phường 2',
    detailedAddress: '456 Đường XYZ',
    fullAddress: '456 Đường XYZ, Phường 2, Quận 3, TP.HCM',
    type: 'sender' as const
  },
  {
    id: '3',
    name: 'Lê Văn C', 
    phone: '0912345678',
    province: 'TP.HCM',
    district: 'Quận 10',
    ward: 'Phường 5',
    detailedAddress: '789 Đường DEF',
    fullAddress: '789 Đường DEF, Phường 5, Quận 10, TP.HCM',
    type: 'sender' as const
  }
];

interface OrderFormProps {
  onClose?: () => void;
  order?: any; // For editing existing orders
}

const OrderForm: React.FC<OrderFormProps> = ({ onClose, order }) => {
  const [formData, setFormData] = useState({
    customOrderId: '',
    sender: order?.sender || '',
    senderPhone: order?.senderPhone || '',
    senderAddress: order?.senderAddress || '',
    senderProvince: order?.senderProvince || '',
    senderDistrict: order?.senderDistrict || '',
    senderWard: order?.senderWard || '',
    senderDetailedAddress: order?.senderDetailedAddress || '',
    senderAddressType: (order?.senderAddressType || 'existing') as 'new' | 'existing',
    recipient: order?.recipient || '',
    recipientPhone: order?.recipientPhone || '',
    address: order?.address || '',
    detailedAddress: order?.detailedAddress || '',
    addressType: (order?.addressType || 'new') as 'new' | 'old',
    province: order?.province || '',
    district: order?.district || '',
    ward: order?.ward || '',
    codAmount: order?.codAmount?.toString() || '',
    paymentBy: (order?.paymentBy || 'recipient') as 'sender' | 'recipient',
    weight: order?.weight?.toString() || '', // Đơn vị: gram
    length: order?.length?.toString() || '', // Chiều dài (mm)
    width: order?.width?.toString() || '', // Chiều rộng (mm)
    height: order?.height?.toString() || '', // Chiều cao (mm)
    packageCount: order?.packageCount?.toString() || '1', // Số lượng kiện hàng
    declaredValue: order?.declaredValue?.toString() || '',
    allowInspection: order?.allowInspection || true, // Cho phép xem hàng
    service: order?.service || '',
    driverNotes: order?.driverNotes || '',
    products: order?.products || [] as OrderProduct[]
  });

  const [estimatedCost, setEstimatedCost] = useState(0);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [selectedSenderAddress, setSelectedSenderAddress] = useState<any>(null);
  const { toast } = useToast();

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProductsChange = (products: OrderProduct[]) => {
    setFormData(prev => ({ ...prev, products }));
    
    // Auto-calculate total weight
    const totalWeight = products.reduce((sum, p) => sum + (p.weight * p.quantity), 0);
    setFormData(prev => ({ ...prev, weight: totalWeight.toString() }));
  };

  const selectAddress = (address: Address, type?: 'sender' | 'recipient') => {
    const addressType = type || address.type;
    
    if (addressType === 'sender') {
      setFormData(prev => ({
        ...prev,
        sender: address.name,
        senderPhone: address.phone,
        senderProvince: address.province,
        senderDistrict: address.district || '',
        senderWard: address.ward,
        senderDetailedAddress: address.detailedAddress,
        senderAddressType: 'existing'
      }));
      setSelectedSenderAddress(address);
    } else {
      setFormData(prev => ({
        ...prev,
        recipient: address.name,
        recipientPhone: address.phone,
        province: address.province,
        district: address.district || '',
        ward: address.ward,
        detailedAddress: address.detailedAddress,
        address: `${address.detailedAddress}, ${address.ward}, ${address.district ? address.district + ', ' : ''}${address.province}`
      }));
    }
    setShowAddressDialog(false);
  };

  const handleSubmit = (e: React.FormEvent, isDraft = false) => {
    e.preventDefault();
    
    if (!isDraft) {
      // Validate required fields for order
      if (!formData.sender || !formData.recipient || !formData.service || !formData.weight) {
        toast({
          title: "Lỗi",
          description: "Vui lòng điền đầy đủ thông tin bắt buộc",
          variant: "destructive",
        });
        return;
      }
    }

    const orderData = {
      ...formData,
      isDraft,
      estimatedCost,
      timestamp: new Date().toISOString()
    };

    console.log('Creating order:', orderData);
    
    toast({
      title: isDraft ? "Đã lưu bản nháp" : "Tạo đơn hàng thành công",
      description: isDraft 
        ? "Bản nháp đã được lưu, bạn có thể chỉnh sửa sau" 
        : `Đơn hàng đã được tạo với mã DH${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
    });

    onClose?.();
  };

  const printLabel = () => {
    toast({
      title: "In tem đơn hàng",
      description: "Chức năng in tem sẽ được triển khai trong phiên bản tiếp theo",
    });
  };

  return (
    <div className="flex flex-col max-h-[100%]">
      {/* Fixed Header */}
      <div className="flex-shrink-0 pb-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800 mb-4">
        <h3 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-1">
          {order ? 'Chỉnh sửa đơn hàng' : 'Tạo đơn hàng mới'}
        </h3>
        <p className="text-blue-700 dark:text-blue-300 text-sm">
          Vui lòng điền đầy đủ thông tin để tạo đơn hàng
        </p>
      </div>
      
      {/* Scrollable Body */}
      <div className="flex-1 overflow-y-auto py-4">
        <form className="space-y-4">
        {/* Row 1: Mã đơn tùy chỉnh - Thu gọn */}
        <Card className="border-l-4 border-l-purple-500 shadow-sm">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-950/30 dark:to-purple-900/20 py-2">
            <CardTitle className="text-sm text-purple-900 dark:text-purple-100 flex items-center">
              <FileText className="h-4 w-4 text-purple-600 dark:text-purple-400 mr-2" />
              Mã đơn hàng tùy chỉnh
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3 pb-3">
            <div className="relative">
              <Input
                id="customOrderId"
                value={formData.customOrderId}
                onChange={(e) => handleInputChange('customOrderId', e.target.value)}
                placeholder="VD: ORD-2024-001 (để trống để tự động tạo)"
                className="h-9 text-sm"
              />
              {formData.customOrderId && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 text-purple-500"
                  onClick={() => handleInputChange('customOrderId', '')}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Row 2: Người gửi */}
        <SenderSection
          formData={formData}
          selectedSenderAddress={selectedSenderAddress}
          mockAddresses={mockSenderAddresses}
          onInputChange={handleInputChange}
          onAddressSelect={selectAddress}
        />

        {/* Row 3: Người nhận & Địa chỉ giao hàng */}
        <RecipientDeliverySection
          formData={formData}
          mockAddresses={mockAddresses}
          showAddressDialog={showAddressDialog}
          onInputChange={handleInputChange}
          onAddressSelect={selectAddress}
          setShowAddressDialog={setShowAddressDialog}
        />

        {/* Row 4: Thông tin gói hàng & COD - Tối ưu layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <Card className="border-l-4 border-l-indigo-500 shadow-sm">
              <CardHeader className="bg-indigo-50 dark:bg-indigo-950/20 py-2">
                <CardTitle className="text-sm text-indigo-900 dark:text-indigo-100">Thông tin gói hàng</CardTitle>
              </CardHeader>
              <CardContent className="pt-3 space-y-3">
                {/* Cân nặng, Số kiện, Dài, Rộng, Cao trên cùng một dòng */}
                <div className="grid grid-cols-5 gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="weight" className="text-xs">Cân nặng (g) *</Label>
                    <Input
                      id="weight"
                      type="number"
                      step="1"
                      value={formData.weight}
                      onChange={(e) => handleInputChange('weight', e.target.value)}
                      placeholder="0"
                      className="h-9 text-sm"
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="packageCount" className="text-xs">Số kiện *</Label>
                    <Select 
                      value={formData.packageCount} 
                      onValueChange={(value) => handleInputChange('packageCount', value)}
                    >
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder="1" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 - Đóng chung</SelectItem>
                        {Array.from({ length: 19 }, (_, i) => i + 2).map((num) => (
                          <SelectItem key={num} value={num.toString()}>
                            {num}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="length" className="text-xs">Dài (mm)</Label>
                    <Input
                      id="length"
                      type="number"
                      value={formData.length}
                      onChange={(e) => handleInputChange('length', e.target.value)}
                      placeholder="0"
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="width" className="text-xs">Rộng (mm)</Label>
                    <Input
                      id="width"
                      type="number"
                      value={formData.width}
                      onChange={(e) => handleInputChange('width', e.target.value)}
                      placeholder="0"
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="height" className="text-xs">Cao (mm)</Label>
                    <Input
                      id="height"
                      type="number"
                      value={formData.height}
                      onChange={(e) => handleInputChange('height', e.target.value)}
                      placeholder="0"
                      className="h-9 text-sm"
                    />
                  </div>
                </div>

                {/* Giá trị khai báo và Dịch vụ trên cùng một dòng */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="declaredValue" className="text-sm">Giá trị khai báo (VNĐ)</Label>
                    <Input
                      id="declaredValue"
                      type="number"
                      value={formData.declaredValue}
                      onChange={(e) => handleInputChange('declaredValue', e.target.value)}
                      placeholder="0"
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="service" className="text-sm">Dịch vụ *</Label>
                    <Select value={formData.service} onValueChange={(value) => handleInputChange('service', value)}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Chọn dịch vụ" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Hỏa tốc">Hỏa tốc (4-6h)</SelectItem>
                        <SelectItem value="Nhanh">Nhanh (24h)</SelectItem>
                        <SelectItem value="Tiết kiệm">Tiết kiệm (48-72h)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Thông tin COD & Thanh toán - Có thêm ghi chú tài xế */}
            <Card className="mt-3 border-l-4 border-l-yellow-500 shadow-sm">
              <CardHeader className="bg-yellow-50 dark:bg-yellow-950/20 py-2">
                <CardTitle className="text-sm text-yellow-900 dark:text-yellow-100">COD & Thanh toán</CardTitle>
              </CardHeader>
              <CardContent className="pt-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="codAmount" className="text-sm">Số tiền thu hộ (VNĐ)</Label>
                    <Input
                      id="codAmount"
                      type="number"
                      value={formData.codAmount}
                      onChange={(e) => handleInputChange('codAmount', e.target.value)}
                      placeholder="0"
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="paymentBy" className="text-sm">Người trả phí</Label>
                    <Select value={formData.paymentBy} onValueChange={(value: 'sender' | 'recipient') => handleInputChange('paymentBy', value)}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sender">Người gửi</SelectItem>
                        <SelectItem value="recipient">Người nhận</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="allowInspection"
                    checked={formData.allowInspection}
                    onChange={(e) => handleInputChange('allowInspection', e.target.checked.toString())}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="allowInspection" className="text-sm">
                    Cho phép xem hàng
                  </Label>
                </div>
                
                {/* Ghi chú cho tài xế - Chuyển từ card riêng vào đây */}
                <div className="space-y-1">
                  <Label htmlFor="driverNotes" className="text-sm">Ghi chú cho tài xế</Label>
                  <Textarea
                    id="driverNotes"
                    value={formData.driverNotes}
                    onChange={(e) => handleInputChange('driverNotes', e.target.value)}
                    placeholder="Nhập ghi chú cho tài xế (nếu có)"
                    rows={2}
                    className="text-sm"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-l-4 border-l-emerald-500 shadow-sm">
            <PriceEstimator 
                weight={parseFloat(formData.weight) || 0}
                service={formData.service}
                province={formData.province}
                declaredValue={parseFloat(formData.declaredValue) || 0}
                onPriceCalculated={setEstimatedCost}
              />
          </Card>
        </div>

        {/* Row 5: Chỉ có Sản phẩm */}
        <Card className="border-l-4 border-l-pink-500 shadow-sm">
          <CardHeader className="py-2">
            <CardTitle className="text-sm">Sản phẩm</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <ProductManager 
              products={formData.products} 
              onProductsChange={handleProductsChange} 
            />
          </CardContent>
        </Card>

        </form>
      </div>
      
      {/* Fixed Footer with action buttons */}
      <div className="flex-shrink-0 pt-4 border-t bg-background space-y-3">
        <div className="flex gap-3">
          {/* Nút đóng màu đỏ */}
          <Button 
            type="button"
            variant="outline" 
            onClick={onClose}
            className="flex-1 border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-400 dark:text-red-400 dark:hover:bg-red-950/20"
          >
            <X className="h-4 w-4 mr-2" />
            Đóng
          </Button>
          
          <Button 
            type="button"
            variant="outline" 
            onClick={() => handleSubmit(new Event('submit') as any, true)}
            className="flex-1"
          >
            <FileText className="h-4 w-4 mr-2" />
            Lưu nháp
          </Button>
          {/* Chỉ hiển thị nút "In tem" khi đang chỉnh sửa đơn hàng đã tồn tại */}
          {order && (
            <Button 
              type="button"
              variant="outline" 
              onClick={printLabel}
              className="flex-1"
            >
              <Printer className="h-4 w-4 mr-2" />
              In tem
            </Button>
          )}
          <Button 
            type="button"
            onClick={(e) => handleSubmit(e, false)}
            className="flex-1 bg-primary hover:bg-primary/90"
          >
            <Save className="h-4 w-4 mr-2" />
            {order ? 'Cập nhật đơn hàng' : 'Tạo đơn hàng'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderForm;
